import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  normalizeTradeValues,
  formatDateTimeLocal,
  parseOptionName,
  formatCurrency,
  formatGroupDate,
  groupByDate,
  getCurrentWeekRange,
  getWeekRange,
  getQuickSearchRange,
} from "../utils";
import "../styles.css";
import API_BASE_URL from "../apiConfig";

const Orders = ({ fetchOrdersRef }) => {
  const [orders, setOrders] = useState([]);
  const [editPopupVisible, setEditPopupVisible] = useState(false);
  const [editOrderData, setEditOrderData] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  // Date and quick search state
  const [dateRange, setDateRange] = useState(getCurrentWeekRange());
  const [selectedQuickSearch, setSelectedQuickSearch] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Collapsed state for each group
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Journal modal state
  const [journalText, setJournalText] = useState("");
  const [journalId, setJournalId] = useState(null);
  const [journalStatus, setJournalStatus] = useState(null); // { type: "success"|"error", message: string }

  // Parse URL params on mount for filters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const filters = {};
    if (params.has("option_name")) {
      filters.option_name = params.get("option_name");
      setSearch(params.get("option_name"));
    }
    // Support start/end date in URI
    if (params.has("start")) {
      filters.start = params.get("start");
    }
    if (params.has("end")) {
      filters.end = params.get("end");
    }
    // If both start/end present, set dateRange accordingly
    if (filters.start && filters.end) {
      setDateRange({ start: filters.start, end: filters.end });
    }
    // Legacy single date param
    if (params.has("date")) {
      filters.date = params.get("date");
      setDateRange({ start: params.get("date"), end: params.get("date") });
    }
    fetchOrders(filters);
    // eslint-disable-next-line
  }, []);

  // Fetch orders with optional filters (including date range)
  const fetchOrders = async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.option_name) params.append("option_name", filters.option_name);
      if (filters.date) params.append("date", filters.date);
      if (filters.start) params.append("start", filters.start);
      if (filters.end) params.append("end", filters.end);
      const url =
        params.toString().length > 0
          ? `${API_BASE_URL}/trades?${params.toString()}`
          : `${API_BASE_URL}/trades`;
      const response = await axios.get(url);
      const normalizedOrders = response.data.map(normalizeTradeValues);
      setOrders(normalizedOrders);
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
  };

  useEffect(() => {
    if (fetchOrdersRef) {
      fetchOrdersRef.current = fetchOrders;
    }
  }, [fetchOrdersRef]);

  // Date/Week navigation handlers (DRY, shared with Trades)
  const handlePrevWeek = useCallback(() => {
    setWeekOffset((prev) => {
      const newOffset = prev - 1;
      const range = getWeekRange(newOffset);
      setDateRange(range);
      fetchOrders({ start: range.start, end: range.end });
      return newOffset;
    });
    setSelectedQuickSearch(null);
  }, []);

  const handleNextWeek = useCallback(() => {
    setWeekOffset((prev) => {
      const newOffset = prev + 1;
      const range = getWeekRange(newOffset);
      setDateRange(range);
      fetchOrders({ start: range.start, end: range.end });
      return newOffset;
    });
    setSelectedQuickSearch(null);
  }, []);

  const handleDateChange = useCallback((e) => {
    const { name, value } = e.target;
    setDateRange((prev) => {
      const newRange = { ...prev, [name]: value };
      fetchOrders({ start: newRange.start, end: newRange.end });
      return newRange;
    });
    setSelectedQuickSearch(null);
  }, []);

  const handleQuickSearch = useCallback((days) => {
    if (selectedQuickSearch === days) {
      setSelectedQuickSearch(null);
      const range = getCurrentWeekRange();
      setDateRange(range);
      fetchOrders({ start: range.start, end: range.end });
    } else {
      const range = getQuickSearchRange(days);
      setSelectedQuickSearch(days);
      setDateRange(range);
      fetchOrders({ start: range.start, end: range.end });
    }
    setSearch("");
  }, [selectedQuickSearch]);

  // Search filter (client-side)
  const filteredOrders = useMemo(() => {
    const searchLower = search.toLowerCase();
    return orders.filter((order) => {
      const orderDate = new Date(order.filled);
      const isWithinDateRange =
        (!dateRange.start || orderDate >= new Date(dateRange.start)) &&
        (!dateRange.end || orderDate <= new Date(dateRange.end + "T23:59:59"));
      return (
        isWithinDateRange &&
        (
          order.option_name.toLowerCase().includes(searchLower) ||
          order.buy_sell.toLowerCase().includes(searchLower) ||
          order.quantity.toString().includes(searchLower) ||
          orderDate.toLocaleString().toLowerCase().includes(searchLower) ||
          order.price.toString().includes(searchLower) ||
          order.fees.toString().includes(searchLower)
        )
      );
    });
  }, [orders, search, dateRange]);

  // Replace groupedOrders logic with groupByDate helper
  const groupedOrdersArr = useMemo(
    () => [...groupByDate(filteredOrders, "filled")].reverse(),
    [filteredOrders]
  );

  const handleClosePopup = () => {
    setShowNewOrderPopup(false);
    setEditPopupVisible(false);
    setEditOrderData([]);
  };

  // Edit Order Functions
  const handleEditPopupOpen = () => {
    const selectedOrders = orders.filter(order => selectedRows.includes(order.id));
    setEditOrderData(selectedOrders);
    setEditPopupVisible(true);
  };

  const handleSaveEditOrder = async () => {
    try {
      await Promise.all(
        editOrderData.map(order => 
          axios.put(`${API_BASE_URL}/trades/${order.id}`, order)
        )
      );
      fetchOrders();
      handleClosePopup();
    } catch (error) {
      console.error("Error updating orders:", error);
    }
  };

  const handleRowClick = (orderId) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(orderId)
        ? prevSelectedRows.filter((id) => id !== orderId)
        : [...prevSelectedRows, orderId]
    );
  };

  const handleEditSelectedRow = () => {
    if (selectedRows.length > 0) {
      const orderToEdit = orders.find((order) => order.id === selectedRows[0]);
      handleEditPopupOpen(orderToEdit);
    }
  };

  // Show confirmation before deleting
  const handleDeleteSelectedRows = async () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await Promise.all(selectedRows.map((id) => axios.delete(`${API_BASE_URL}/trades/${id}`)));
      fetchOrders();
      setSelectedRows([]);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Error deleting selected orders:", error);
      setShowDeleteConfirm(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  // Collapse/Expand all handlers
  const collapseAll = useCallback(() => {
    const collapsed = {};
    groupedOrdersArr.forEach(group => {
      collapsed[group.dateKey] = true;
    });
    setCollapsedGroups(collapsed);
  }, [groupedOrdersArr]);

  const expandAll = useCallback(() => {
    const expanded = {};
    groupedOrdersArr.forEach(group => {
      expanded[group.dateKey] = false;
    });
    setCollapsedGroups(expanded);
  }, [groupedOrdersArr]);

  // Calculate open/closed contracts for a group
  const calcOpenClosedContracts = (items) => {
    let open = 0, closed = 0;
    items.forEach(order => {
      if (order.buy_sell === "Buy") open += Number(order.quantity);
      if (order.buy_sell === "Sell") closed += Number(order.quantity);
    });
    return { open, closed };
  };
  
  // Compute trade summary for all visible orders if all have the same symbol/strike/exp and both Buy and Sell exist
  const tradeSummary = useMemo(() => {
    if (filteredOrders.length === 0) return null;
    const { symbol, strike, exp } = parseOptionName(filteredOrders[0].option_name);
    const allSame = filteredOrders.every(
      o => {
        const parsed = parseOptionName(o.option_name);
        return parsed.symbol === symbol && parsed.strike === strike && parsed.exp === exp;
      }
    );
    const hasBuy = filteredOrders.some(o => o.buy_sell === "Buy");
    const hasSell = filteredOrders.some(o => o.buy_sell === "Sell");
    if (allSame && hasBuy && hasSell) {
      const buyOrders = filteredOrders.filter(o => o.buy_sell === "Buy");
      const sellOrders = filteredOrders.filter(o => o.buy_sell === "Sell");
      const totalBuyContracts = buyOrders.reduce((sum, o) => sum + Number(o.quantity), 0);
      const totalSellContracts = sellOrders.reduce((sum, o) => sum + Number(o.quantity), 0);
      const avgBuyPrice = totalBuyContracts > 0
        ? buyOrders.reduce((sum, o) => sum + Number(o.price) * Number(o.quantity), 0) / totalBuyContracts
        : null;
      const avgSellPrice = totalSellContracts > 0
        ? sellOrders.reduce((sum, o) => sum + Number(o.price) * Number(o.quantity), 0) / totalSellContracts
        : null;
      // Add date_opened and date_closed for journal lookup
      const date_opened = buyOrders.length > 0 ? buyOrders[0].filled.slice(0, 10) : null;
      const date_closed = sellOrders.length > 0 ? sellOrders[sellOrders.length - 1].filled.slice(0, 10) : null;
      return {
        symbol,
        strike,
        exp,
        avgBuyPrice,
        avgSellPrice,
        totalBuyContracts,
        totalSellContracts,
        date_opened,
        date_closed,
        type: buyOrders[0]?.option_name?.includes("swingtrade") ? "swingtrade" : undefined
      };
    }
    return null;
  }, [filteredOrders]);

  // Fetch journal entry for the trade summary on mount or when tradeSummary changes
  useEffect(() => {
    const fetchJournal = async () => {
      setJournalText("");
      setJournalId(null);
      if (!tradeSummary) return;
      try {
        const res = await axios.get(`${API_BASE_URL}/trade-journal`, {
          params: {
            option_name: `${tradeSummary.symbol} ${tradeSummary.strike}${tradeSummary.type || ""} ${tradeSummary.exp}`,
            date_opened: tradeSummary.date_opened,
            date_closed: tradeSummary.date_closed,
          },
        });
        if (res.data && res.data.journal) {
          setJournalText(res.data.journal);
          setJournalId(res.data.id);
        }
      } catch (e) {
        setJournalText("");
        setJournalId(null);
      }
    };
    fetchJournal();
    // eslint-disable-next-line
  }, [tradeSummary]);

  // Save, update, or delete journal
  const handleJournalSave = async () => {
    if (!tradeSummary) return;
    try {
      if (journalText.trim() === "") {
        // Delete if exists
        if (journalId) {
          await axios.delete(`${API_BASE_URL}/trade-journal/${journalId}`);
          setJournalId(null);
          setJournalText("");
          setJournalStatus({ type: "success", message: "Journal deleted." });
        }
      } else if (journalId) {
        // Update
        await axios.put(`${API_BASE_URL}/trade-journal/${journalId}`, {
          journal: journalText,
        });
        setJournalStatus({ type: "success", message: "Journal updated." });
      } else {
        // Create
        await axios.post(`${API_BASE_URL}/trade-journal`, {
          option_name: `${tradeSummary.symbol} ${tradeSummary.strike}${tradeSummary.type || ""} ${tradeSummary.exp}`,
          date_opened: tradeSummary.date_opened,
          date_closed: tradeSummary.date_closed,
          journal: journalText,
        });
        setJournalStatus({ type: "success", message: "Journal saved." });
      }
      setTimeout(() => setJournalStatus(null), 2000);
    } catch (e) {
      setJournalStatus({ type: "error", message: "Error saving journal." });
      setTimeout(() => setJournalStatus(null), 3000);
    }
    // Optionally reload after a delay if you want to refresh the summary
    // setTimeout(() => window.location.reload(), 300);
  };

  return (
    <div className="container">
      {/* Flash message for journal status */}
      {journalStatus && (
        <div
          style={{
            position: "fixed",
            top: 24,
            right: 24,
            zIndex: 2000,
            minWidth: 220,
            padding: "12px 20px",
            borderRadius: 8,
            background: journalStatus.type === "success" ? "#d1e7dd" : "#f8d7da",
            color: journalStatus.type === "success" ? "#0f5132" : "#842029",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            fontWeight: 500,
            transition: "opacity 0.2s"
          }}
        >
          {journalStatus.message}
        </div>
      )}
      <h1 className="d-flex align-items-center">
        Orders
        <div className="quick-search-buttons ms-3">
          <button
            onClick={() => handleQuickSearch(1)}
            className={`btn btn-outline-secondary me-2 ${selectedQuickSearch === 1 ? "btn-primary text-white" : ""}`}
          >
            Today
          </button>
          <button
            onClick={() => handleQuickSearch(5)}
            className={`btn btn-outline-secondary me-2 ${selectedQuickSearch === 5 ? "btn-primary text-white" : ""}`}
          >
            Week
          </button>
          <button
            onClick={() => handleQuickSearch(30)}
            className={`btn btn-outline-secondary ${selectedQuickSearch === 30 ? "btn-primary text-white" : ""}`}
          >
            Month
          </button>
        </div>
      </h1>
      {/* Controls: Week Navigation, Calendar, Search, Quick Search */}
      <div className="d-flex flex-wrap align-items-center mb-3 gap-2">
        <div className="d-flex align-items-center me-3 mb-2">
          <button className="btn btn-outline-secondary btn-sm me-2" onClick={handlePrevWeek}>
            &lt; Prev
          </button>
          <div className="calendar-search d-flex align-items-center mb-2 ms-2">
            <input
              type="date"
              name="start"
              value={dateRange.start}
              onChange={handleDateChange}
              className="form-control form-control-sm me-2"
              style={{ width: 140 }}
            />
            <span className="me-2">to</span>
            <input
              type="date"
              name="end"
              value={dateRange.end}
              onChange={handleDateChange}
              className="form-control form-control-sm"
              style={{ width: 140 }}
            />
          </div>
          <button className="btn btn-outline-secondary btn-sm ms-2" onClick={handleNextWeek}>
            Next &gt;
          </button>
        </div>
        <input
          type="text"
          placeholder="Search by option name"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="search-bar me-2 mb-2"
          style={{ maxWidth: 220 }}
        />
        {/* Remove quick search buttons from here */}
        {/* Only show Edit/Delete when a row is selected */}
        {selectedRows.length > 0 && (
          <div>
            <button 
              className="btn btn-outline-primary me-2"
              onClick={handleEditSelectedRow}
            >
              Edit
            </button>
            <button 
              className="btn btn-outline-danger me-2"
              onClick={handleDeleteSelectedRows}
            >
              Delete
            </button>
          </div>
        )}
        <div className="d-flex align-items-center mb-2 ms-3">
          <button className="btn btn-sm btn-outline-secondary me-2" onClick={collapseAll}>
            Collapse All
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={expandAll}>
            Expand All
          </button>
        </div>
      </div>
      {/* Trade summary for all filtered orders */}
      {tradeSummary && (
        <div className="trade-summary mb-2" style={{ background: "#f8f9fa", border: "1px solid #e0e0e0", borderRadius: 6, padding: 8, marginBottom: 8 }}>
          <strong>Trade Summary:</strong>
          <span style={{ marginLeft: 12 }}>
            {tradeSummary.symbol} {tradeSummary.strike} {tradeSummary.exp}
          </span>
          <span style={{ marginLeft: 18, color: "#1a7f37" }}>
            Avg Buy Price: {tradeSummary.avgBuyPrice !== null ? formatCurrency(tradeSummary.avgBuyPrice) : "N/A"}
          </span>
          <span style={{ marginLeft: 18, color: "#c0392b" }}>
            Avg Sell Price: {tradeSummary.avgSellPrice !== null ? formatCurrency(tradeSummary.avgSellPrice) : "N/A"}
          </span>
          <span style={{ marginLeft: 18 }}>
            Bought: {tradeSummary.totalBuyContracts}
          </span>
          <span style={{ marginLeft: 18 }}>
            Sold: {tradeSummary.totalSellContracts}
          </span>
          {/* Inline Journal Field */}
          <div style={{ marginTop: 12 }}>
            <label htmlFor="trade-journal" style={{ fontWeight: 500, marginRight: 8 }}>Journal:</label>
            <textarea
              id="trade-journal"
              className="form-control"
              rows={3}
              style={{ display: "inline-block", width: "60%", verticalAlign: "middle", marginRight: 8 }}
              value={journalText}
              onChange={e => setJournalText(e.target.value)}
              placeholder="Write your journal entry here..."
            />
            <button
              className="btn btn-success"
              style={{ verticalAlign: "middle" }}
              onClick={handleJournalSave}
              disabled={journalText === "" && !journalId}
              title={journalText.trim() === "" && journalId ? "Delete" : "Save"}
            >
              {journalText.trim() === "" && journalId ? "Delete" : "Save"}
            </button>
          </div>
        </div>
      )}
      {/* Single table for all orders */}
      <table className="table table-striped table-bordered table-hover">
        <thead>
          <tr>
            <th>Symbol</th>
            <th>Strike</th>
            <th>Exp</th>
            <th>Buy/Sell</th>
            <th>Quantity</th>
            <th>Filled</th>
            <th>Price</th>
            <th>Fees</th>
          </tr>
        </thead>
        <tbody>
          {groupedOrdersArr.map(({ dateKey, items }) => {
            // Remove tradeInfo and trade summary row from here
            return (
              <React.Fragment key={dateKey}>
                {/* Group header row */}
                <tr
                  className="table-group-header"
                  style={{ background: "#f5f5f5", cursor: "pointer" }}
                  onClick={() =>
                    setCollapsedGroups((prev) => ({
                      ...prev,
                      [dateKey]: !prev[dateKey],
                    }))
                  }
                >
                  <td colSpan={8} style={{ fontWeight: "bold" }}>
                    {formatGroupDate(dateKey)}
                    <span style={{ marginLeft: 16 }}>
                      | Orders: {items.length}
                    </span>
                    <span style={{ marginLeft: 16 }}>
                      | Open Contracts: {calcOpenClosedContracts(items).open}
                    </span>
                    <span style={{ marginLeft: 16 }}>
                      | Closed Contracts: {calcOpenClosedContracts(items).closed}
                    </span>
                    <span style={{ float: "right" }}>
                      {collapsedGroups[dateKey] ? "▶" : "▼"}
                    </span>
                  </td>
                </tr>
                {/* Orders for this group */}
                {!collapsedGroups[dateKey] &&
                  items.map((order) => {
                    const { symbol, strike, exp } = parseOptionName(order.option_name);
                    const isBuy = order.buy_sell === "Buy";
                    return (
                      <tr
                        key={order.id}
                        onClick={() => handleRowClick(order.id)}
                        className={
                          [
                            selectedRows.includes(order.id) ? "selected" : "",
                          ].join(" ")
                        }
                      >
                        <td className="group-name">{symbol}</td>
                        <td
                          className="group-name"
                          style={{
                            color: strike.includes("C") ? "blue" : strike.includes("P") ? "orange" : "inherit"
                          }}
                        >
                          {strike}
                        </td>
                        <td className="group-name">{exp}</td>
                        <td className={isBuy ? "group-buy" : "group-sell"}>
                          {order.buy_sell}
                        </td>
                        <td className={isBuy ? "group-buy" : "group-sell"}>
                          {order.quantity}
                        </td>
                        <td>{new Date(order.filled).toLocaleString()}</td>
                        <td>{formatCurrency(order.price)}</td>
                        <td>{formatCurrency(order.fees)}</td>
                      </tr>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {/* Edit Order Popup */}
      {editPopupVisible && (
        <div className="custom-modal">
          <div className="custom-modal-content">
            <button className="close-button" onClick={handleClosePopup}>
              X
            </button>
            <h2>Edit Orders</h2>
            <table className="custom-popup-table">
              <thead>
                <tr>
                  <th>Option Name</th>
                  <th>Buy/Sell</th>
                  <th>Quantity</th>
                  <th>Filled</th>
                  <th>Price</th>
                  <th>Fees</th>
                </tr>
              </thead>
              <tbody>
                {editOrderData.map((order, index) => (
                  <tr key={order.id}>
                    <td>
                      <input
                        type="text"
                        value={order.option_name}
                        onChange={(e) => handleFieldChange(index, "option_name", e.target.value, true)}
                      />
                    </td>
                    <td>
                      <select
                        value={order.buy_sell}
                        onChange={(e) => handleFieldChange(index, "buy_sell", e.target.value, true)}
                      >
                        <option value="Buy">Buy</option>
                        <option value="Sell">Sell</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        value={order.quantity}
                        onChange={(e) => handleFieldChange(index, "quantity", e.target.value, true)}
                      />
                    </td>
                    <td>
                      <input
                        type="datetime-local"
                        value={formatDateTimeLocal(order.filled)}
                        onChange={(e) => handleFieldChange(index, "filled", e.target.value, true)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={order.price}
                        onChange={(e) => handleFieldChange(index, "price", e.target.value, true)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={order.fees}
                        onChange={(e) => handleFieldChange(index, "fees", e.target.value, true)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button className="btn btn-primary" onClick={handleSaveEditOrder}>
              Save Changes
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {showDeleteConfirm && (
        <div className="custom-modal">
          <div className="custom-modal-content">
            <h2>Confirm Delete</h2>
            <p>Are you sure you want to delete the selected order{selectedRows.length > 1 ? "s" : ""}?</p>
            <div className="custom-popup-actions">
              <button className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              <button className="btn btn-secondary" onClick={cancelDelete}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
